import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "@/lib/utils"

const badgeVariants = cva(
  "inline-flex items-center justify-center rounded-md border px-2 py-0.5 text-xs font-medium w-fit whitespace-nowrap shrink-0 [&>svg]:size-3 gap-1 [&>svg]:pointer-events-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive transition-[color,box-shadow] overflow-hidden",
  {
    variants: {
      variant: {
        default:
          "border-transparent bg-primary text-primary-foreground [a&]:hover:bg-primary/90",
        secondary:
          "border-transparent bg-secondary text-secondary-foreground [a&]:hover:bg-secondary/90",
        destructive:
          "border-transparent bg-destructive text-white [a&]:hover:bg-destructive/90 focus-visible:ring-destructive/20 dark:focus-visible:ring-destructive/40 dark:bg-destructive/60",
        outline:
          "text-foreground [a&]:hover:bg-accent [a&]:hover:text-accent-foreground",

        new_default:
          "bg-transparent border-1 border-royal-glow-3-light text-royal-glow-3-light rounded-md text-xs font-normal py-0.5 px-2 hover:bg-royal-glow-5 focus-visible:border-royal-glow-3 focus-visible:text-royal-glow-3",
        new_secondary:
          "bg-transparent border border-neutral-200 text-zinc-400 rounded-md text-xs font-normal py-0.5 px-2 focus-visible:font-semibold focus-visible:border-custom-outline focus-visible:bg-secondary-badge focus-visible:text-neutral-700 focus-visible:outline-1 focus-visible:outline-custom-outline focus-visible:outline-offset-[1px] focus-visible:ring-0",
        new_outline:
          "bg-transparent border-0 text-neutral-400 rounded-md text-xs font-normal py-0.5 px-2 focus-visible:bg-neutral-100",
        new_destructive:
          "bg-custom-destructive-dark border border-transparent text-white rounded-md text-xs font-semibold py-0.5 px-2 overflow-visible outline-none focus-visible:ring-1 focus-visible:ring-custom-destructive-dark/40 focus-visible:ring-offset-1 focus-visible:ring-offset-white focus-visible:border-custom-outline/50",
        verified:
          "bg-custom-dark-blue border border-transparent text-white rounded-md text-xs font-semibold py-0.5 px-2 overflow-visible outline-none focus-visible:ring-1 focus-visible:ring-monochrome-core-3 focus-visible:ring-offset-1 focus-visible:ring-offset-white focus-visible:border-custom-outline/50",

        number_default:
          "bg-royal-glow-3 border-1 border-transparent text-white rounded-full text-xs font-semibold py-0.5 px-1.5 focus-visible:border-custom-outline/50 focus-visible:text-white focus-visible:ring-1 focus-visible:ring-offset-1 focus-visible:ring-custom-outline",
        number_secondary:
          "bg-secondary-badge border-1 border-transparent text-zinc-400 rounded-full text-xs font-semibold py-0.5 px-1.5 focus-visible:border-custom-outline focus-visible:bg-secondary-badge focus-visible:text-neutral-700 focus-visible:outline-1 focus-visible:outline-custom-outline focus-visible:outline-offset-[1px] focus-visible:ring-0",
        number_outline:
          "bg-transparent border-1 border-base-color text-black rounded-full text-xs font-semibold py-0.5 px-1.5 focus-visible:bg-neutral-100 focus-visible:border-custom-outline focus-visible:bg-secondary-badge focus-visible:text-neutral-700 focus-visible:outline-1 focus-visible:outline-custom-outline focus-visible:outline-offset-[1px] focus-visible:ring-0",
        number_destructive:
          "bg-custom-destructive-dark border border-transparent text-white rounded-full text-xs font-semibold py-0.5 px-1.5 overflow-visible outline-none focus-visible:ring-1 focus-visible:ring-custom-destructive-dark/40 focus-visible:ring-offset-1 focus-visible:ring-offset-white focus-visible:border-custom-outline/50",

        status_new:
          "border-1 border-light-orange/50 text-light-orange bg-light-orange/5 rounded-md text-xs font-normal py-1 px-2",
        status_in_progress:
          "border-1 border-dark-indigo/50 text-dark-indigo bg-dark-indigo/5 rounded-md text-xs font-normal py-1 px-2",
        status_approved:
          "border-1 border-blue-magenta/50 text-blue-magenta bg-blue-magenta/5 rounded-md text-xs font-normal py-1 px-2",
        status_done:
          "border-1 border-light-green/50 text-light-green bg-light-green/5 rounded-md text-xs font-normal py-1 px-2",
        status_canceled:
          "border-1 border-dark-orange/50 text-dark-orange bg-dark-orange/5 rounded-md text-xs font-normal py-1 px-2",

        filled_status_new:
          "text-white bg-fresh-orange rounded-md text-xs font-normal py-1 px-2",
        filled_status_in_progress:
          "text-white bg-blue-500 rounded-md text-xs font-normal py-1 px-2",
        filled_status_completed:
          "text-white bg-green-500 rounded-md text-xs font-normal py-1 px-2",
        filled_status_canceled:
          "text-white bg-red-600 rounded-md text-xs font-normal py-1 px-2",

        service_gray:
          "text-white bg-gray/50 rounded-2xl text-sm font-semibold py-0.5 px-2",
        service_gray_sm:
          "text-white bg-gray/50 rounded-2xl text-2xs font-normal py-0.5 px-2",
        service_blue:
          "text-slate-800 bg-slate-200 rounded-2xl text-sm font-normal py-0.5 px-2",
        service_black:
          "text-white bg-black rounded-lg text-xs font-semibold py-0.5 px-2",
        service_white:
          'text-black bg-white rounded-lg text-xs font-semibold py-0.5 px-2',
        service_secondary:
          "text-black bg-secondary-badge rounded-lg text-xs font-semibold py-0.5 px-2",
        success: "border-transparent bg-green-100 text-green-800 [a&]:hover:bg-green-200",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  }
)

function Badge({
  className,
  variant,
  asChild = false,
  ...props
}: React.ComponentProps<"span"> &
  VariantProps<typeof badgeVariants> & { asChild?: boolean }) {
  const Comp = asChild ? Slot : "span"

  return (
    <Comp
      data-slot="badge"
      className={cn(badgeVariants({ variant }), className)}
      {...props}
    />
  )
}

export { Badge, badgeVariants }
